import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { buildBody } from '../data/microbes';
import {
  PALETTE as INKS,
  closureRevolutions,
  penPoint,
  wheelCentre,
} from '../data/spirograph';
import { PALETTE as MILL_INK, SHAPES as MILL_SHAPES, addShape, symmetryImages } from '../data/crystal';

/**
 * The small moving figure at the top of each Data Essays card.
 *
 * Each one is a miniature of the technique its essay is built on, so the card shows
 * what the piece *does* rather than describing it. None of them touch the essays'
 * real data: the landing page should not pull a 200KB geojson or a corpus file to
 * decorate a link, so the shapes here are generated or hand-written.
 *
 * They share one canvas, one rAF and one set of guards:
 *
 *   - nothing animates off screen. Four permanently-warm loops on a landing page is
 *     exactly the sort of thing that makes a site feel heavy for no return.
 *   - `prefers-reduced-motion` paints a single representative frame and stops.
 *   - they are inert. These sit inside a `<Link>`, so anything interactive here would
 *     be fighting the click target that is the point of the card.
 */
const H = 132;
const FPS = 30; // plenty for figures this size, and a third of the work of 90

const PALETTE = {
  circlepack: ['#1e2a3a', '#2f4a6b', '#4a7fb5', '#8ab4e0'],
  heatmap: ['#161326', '#3d2d63', '#8a6bb1', '#c25b4e', '#e8a04a'],
  globe: ['#123d2a', '#1fa159', '#4ade80'],
};

/* ---------------------------------------------------------------- circle pack */

/**
 * A stand-in corpus. Shaped like the real thing — a few big branches, each with a
 * spread of leaves of uneven weight — because a pack of equal circles reads as a
 * grid and misses the point of the technique.
 */
const FAKE_TREE = {
  name: 'corpus',
  children: [
    {
      name: 'a',
      children: [
        { name: 'a1', children: [{ value: 9 }, { value: 5 }, { value: 3 }, { value: 2 }] },
        { name: 'a2', children: [{ value: 6 }, { value: 4 }, { value: 2 }] },
        { name: 'a3', children: [{ value: 4 }, { value: 3 }] },
      ],
    },
    {
      name: 'b',
      children: [
        { name: 'b1', children: [{ value: 7 }, { value: 4 }, { value: 3 }, { value: 2 }] },
        { name: 'b2', children: [{ value: 5 }, { value: 3 }, { value: 2 }] },
      ],
    },
    {
      name: 'c',
      children: [
        { name: 'c1', children: [{ value: 5 }, { value: 3 }, { value: 2 }] },
        { name: 'c2', children: [{ value: 3 }, { value: 2 }] },
      ],
    },
  ],
};

const makePack = () => {
  const root = d3
    .hierarchy(FAKE_TREE)
    .sum((d) => d.value || 0)
    .sort((a, b) => b.value - a.value);
  d3.pack().size([H, H]).padding(3)(root);

  // The tour: whole corpus, into a branch, into one of its clusters, back out.
  const branch = root.children[0];
  const leaf = branch.children[0];
  const stops = [root, branch, leaf, branch, root];
  return { root, stops: stops.map((n) => [n.x, n.y, n.r * 2.15]) };
};

const drawPack = (ctx, w, time, st) => {
  const LEG = 1900; // ms per move, with a beat of stillness at each end
  const total = st.stops.length - 1;
  const pos = (time % (LEG * total)) / LEG;
  const i = Math.min(total - 1, Math.floor(pos));
  const e = d3.easeCubicInOut(Math.max(0, Math.min(1, (pos - i - 0.12) / 0.76)));
  const view = d3.interpolateZoom(st.stops[i], st.stops[i + 1])(e);

  const k = H / view[2];
  ctx.save();
  ctx.translate(w / 2, H / 2);
  ctx.scale(k, k);
  ctx.translate(-view[0], -view[1]);

  st.root.descendants().forEach((n) => {
    const shade = PALETTE.circlepack[Math.min(n.depth, PALETTE.circlepack.length - 1)];
    ctx.beginPath();
    ctx.arc(n.x, n.y, n.r, 0, 2 * Math.PI);
    ctx.fillStyle = shade;
    ctx.globalAlpha = n.children ? 0.5 : 0.95;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.lineWidth = 0.6 / k;
    ctx.strokeStyle = 'rgba(255,255,255,0.22)';
    ctx.stroke();
  });
  ctx.restore();
};

/* -------------------------------------------------------------------- heatmap */

const COLS = 26;
const ROWS = 8;

const makeHeat = () => {
  const cur = new Float32Array(COLS * ROWS);
  const tgt = new Float32Array(COLS * ROWS);
  for (let i = 0; i < cur.length; i += 1) {
    cur[i] = Math.random();
    tgt[i] = cur[i];
  }
  return { cur, tgt, ramp: d3.interpolateRgbBasis(PALETTE.heatmap), last: 0 };
};

const drawHeat = (ctx, w, time, st) => {
  // Reroll a slice of the grid on a beat, then ease towards it — a whole-grid
  // reroll every frame is noise, and easing alone just breathes.
  if (time - st.last > 110) {
    st.last = time;
    for (let n = 0; n < 26; n += 1) {
      st.tgt[Math.floor(Math.random() * st.tgt.length)] = Math.random();
    }
  }
  const pad = 2;
  const cw = (w - pad) / COLS;
  const ch = (H - pad) / ROWS;

  for (let r = 0; r < ROWS; r += 1) {
    for (let c = 0; c < COLS; c += 1) {
      const i = r * COLS + c;
      st.cur[i] += (st.tgt[i] - st.cur[i]) * 0.12;
      // A standing wave under the noise, so the grid reads as structured rather
      // than as static.
      const wave = 0.5 + 0.5 * Math.sin(c * 0.42 + r * 0.7 + time / 620);
      ctx.fillStyle = st.ramp(Math.min(1, st.cur[i] * 0.62 + wave * 0.38));
      ctx.fillRect(pad + c * cw, pad + r * ch, cw - 1.4, ch - 1.4);
    }
  }
};

/* --------------------------------------------------------------------- globe */

// Washington out to a scatter of posts, as [lon, lat]. Hand-written: the card should
// not fetch the essay's geojson just to draw a decoration.
const ORIGIN = [-77.04, 38.91];
const POSTS = [
  [-58.4, -34.6], [-99.1, 19.4], [-46.6, -23.6], [2.35, 48.86], [13.4, 52.5],
  [37.6, 55.75], [116.4, 39.9], [139.7, 35.7], [77.2, 28.6], [31.2, 30.0],
  [18.4, -33.9], [151.2, -33.9], [-0.13, 51.5], [55.3, 25.2], [103.8, 1.35],
];

const makeGlobe = () => {
  const projection = d3
    .geoOrthographic()
    .fitExtent([[4, 4], [H - 4, H - 4]], { type: 'Sphere' });
  return { projection, path: d3.geoPath(projection), grat: d3.geoGraticule10() };
};

const drawGlobe = (ctx, w, time, st) => {
  const { projection } = st;
  projection.rotate([time / 42 - 60, -14]);
  const path = st.path.context(ctx);

  ctx.save();
  ctx.translate((w - H) / 2, 0);

  ctx.beginPath();
  path({ type: 'Sphere' });
  ctx.fillStyle = '#080d16';
  ctx.fill();

  ctx.beginPath();
  path(st.grat);
  ctx.strokeStyle = 'rgba(255,255,255,0.10)';
  ctx.lineWidth = 0.5;
  ctx.stroke();

  // Geodesics, clipped to the near hemisphere by geoPath for free.
  ctx.lineWidth = 1.1;
  ctx.strokeStyle = PALETTE.globe[1];
  ctx.globalAlpha = 0.75;
  POSTS.forEach((p) => {
    ctx.beginPath();
    path({ type: 'LineString', coordinates: [ORIGIN, p] });
    ctx.stroke();
  });
  ctx.globalAlpha = 1;

  const front = (c) => {
    const r = d3.geoRotation(projection.rotate())(c);
    return r[0] > -90 && r[0] < 90;
  };
  POSTS.concat([ORIGIN]).forEach((c) => {
    if (!front(c)) return;
    const p = projection(c);
    if (!p) return;
    ctx.beginPath();
    ctx.arc(p[0], p[1], c === ORIGIN ? 2.6 : 1.6, 0, 2 * Math.PI);
    ctx.fillStyle = c === ORIGIN ? '#ffffff' : PALETTE.globe[2];
    ctx.fill();
  });

  ctx.beginPath();
  path({ type: 'Sphere' });
  ctx.strokeStyle = 'rgba(255,255,255,0.28)';
  ctx.lineWidth = 0.8;
  ctx.stroke();
  ctx.restore();
};

/* ------------------------------------------------------------------- microbe */

const makeMicrobe = () => {
  // The essay's own generator, on a fixed seed so the card is the same creature
  // every visit rather than a different one on each mount.
  const body = buildBody(24, { hue0: 168, hueStep: 34, symmetry: 6, seed: 11 });
  const maxR = d3.max(body.nodes, (n) => n.restR) || 1;
  const fit = (H / 2 - 12) / maxR;
  return { body, fit };
};

const drawMicrobe = (ctx, w, time, st) => {
  const { body, fit } = st;
  const spin = time / 5200;

  const at = (n) => {
    // Breathing travels round the body as a wave rather than pulsing as one beat.
    const pulse = 1 + 0.075 * Math.sin(time / 520 + n.phase);
    const a = n.angle + spin;
    const rad = n.restR * fit * pulse;
    return [w / 2 + Math.cos(a) * rad, H / 2 + Math.sin(a) * rad];
  };

  ctx.lineWidth = 1;
  body.edges.forEach((e) => {
    const a = at(body.nodes[e.source]);
    const b = at(body.nodes[e.target]);
    ctx.beginPath();
    ctx.moveTo(a[0], a[1]);
    ctx.lineTo(b[0], b[1]);
    ctx.strokeStyle =
      e.kind === 'chord' ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.3)';
    ctx.stroke();
  });

  body.nodes.forEach((n) => {
    const [x, y] = at(n);
    ctx.beginPath();
    ctx.arc(x, y, Math.max(1, n.r * fit * 0.42), 0, 2 * Math.PI);
    ctx.fillStyle = n.color;
    ctx.globalAlpha = 0.92;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.lineWidth = 0.7;
    ctx.strokeStyle = 'rgba(5,5,7,0.55)';
    ctx.stroke();
  });
};

/* ----------------------------------------------------------------- spirograph */

/**
 * Smaller gears than the page uses. 36/14 closes after 7 revolutions with 18 petals,
 * so the figure completes inside a loop short enough to watch twice — the real
 * 96/52 would need 13 revolutions and most of a minute.
 */
const SPIRO = { N: 36, n: 14 };

const makeSpiro = () => ({
  end: Math.PI * 2 * closureRevolutions(SPIRO.N, SPIRO.n),
  pens: [
    { d: 0.9, ink: INKS[0] },
    { d: 0.52, ink: INKS[2] },
  ],
});

const drawSpiro = (ctx, w, time, st) => {
  const DRAW = 8200;
  const HOLD = 1400; // the finished pattern sits for a beat before it starts again
  const phase = time % (DRAW + HOLD);
  const p = Math.min(1, phase / DRAW);
  const theta = p * st.end;

  const R = H / 2 - 7;
  const r = (R * SPIRO.n) / SPIRO.N;
  const cx = w / 2;
  const cy = H / 2;

  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(255,255,255,0.16)';
  ctx.lineWidth = 1;
  ctx.stroke();

  const STEP = 0.02;
  st.pens.forEach((pen) => {
    const d = pen.d * r;
    ctx.beginPath();
    for (let a = 0; a <= theta; a += STEP) {
      const q = penPoint(R, r, d, 0, a);
      if (a === 0) ctx.moveTo(cx + q[0], cy + q[1]);
      else ctx.lineTo(cx + q[0], cy + q[1]);
    }
    ctx.strokeStyle = pen.ink;
    ctx.lineWidth = 1.1;
    ctx.stroke();
  });

  // The mechanism only shows while it is drawing; once the pattern closes it is the
  // drawing that matters, and a wheel parked on top of it just adds clutter.
  if (p < 1) {
    const [wx, wy] = wheelCentre(R, r, theta);
    ctx.beginPath();
    ctx.arc(cx + wx, cy + wy, r, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.24)';
    ctx.stroke();
    st.pens.forEach((pen) => {
      const q = penPoint(R, r, pen.d * r, 0, theta);
      ctx.beginPath();
      ctx.arc(cx + q[0], cy + q[1], 2.6, 0, Math.PI * 2);
      ctx.fillStyle = pen.ink;
      ctx.fill();
    });
  }
};

/* ------------------------------------------------------------------- the mill */

/**
 * A miniature of the toy: two ropes extruding outward under twelve-fold symmetry,
 * one of them splitting at a ring.
 *
 * The whole run is simulated once at build time into a flat list of stamps carrying
 * the moment each was laid down, so the animation is a filter on that list rather
 * than a live particle system. Stamp spacing is much coarser than the real thing —
 * at 132px a faithful density is a solid disc.
 */
const MILL_RINGS = [30, 46];

const makeMill = () => {
  const R = H / 2 - 5;
  const C = 90; // area rate, matched to reach the rim inside the cycle
  const stamps = [];

  const walk = (off, r0, born, sides, ink0, size, depth) => {
    let ink = ink0;
    let r = r0;
    let t = born;
    let next = MILL_RINGS.findIndex((x) => x > r0);
    if (next < 0) next = MILL_RINGS.length;

    while (r < R && t - born < 40) {
      t += 0.7;
      r = Math.sqrt(r0 * r0 + 2 * C * (t - born));
      if (next < MILL_RINGS.length && r >= MILL_RINGS[next]) {
        const at = MILL_RINGS[next];
        next += 1;
        if (depth === 0) {
          // The parent parks at the junction and three children carry on — the same
          // crystal rule the page itself uses.
          stamps.push({ r: at, a: off + t, size: size * 1.5, sides, ink, t });
          for (let i = -1; i <= 1; i += 1) {
            walk(off + i * 0.17, at, t, sides, (ink + 2 + i + MILL_INK.length) % MILL_INK.length, size * 0.7, depth + 1);
          }
          return;
        }
        ink = (ink + 1) % MILL_INK.length;
      }
      stamps.push({ r, a: off + t, size, sides, ink, t });
    }
  };

  walk(0.25, 11, 0, 0, 0, 4.4, 0); // triangle
  walk(0.78, 11, 0, 2, 2, 4.4, 0); // hexagon

  const span = d3.max(stamps, (x) => x.t) || 1;
  stamps.forEach((x) => {
    x.t /= span;
  });
  stamps.sort((a, b) => a.t - b.t);
  return { stamps, buf: new Float64Array(36) };
};

const drawMill = (ctx, w, time, st) => {
  const DRAW = 9000;
  const HOLD = 1600;
  const p = Math.min(1, (time % (DRAW + HOLD)) / DRAW);
  const cx = w / 2;
  const cy = H / 2;

  ctx.strokeStyle = 'rgba(255,255,255,0.13)';
  ctx.lineWidth = 1;
  MILL_RINGS.forEach((r) => {
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
  });

  // One path per ink: a full frame is five fills rather than a couple of thousand.
  const buckets = new Map();
  for (let i = 0; i < st.stamps.length; i += 1) {
    const stamp = st.stamps[i];
    if (stamp.t > p) break;
    let path = buckets.get(stamp.ink);
    if (!path) {
      path = new Path2D();
      buckets.set(stamp.ink, path);
    }
    symmetryImages(stamp.r, stamp.a, st.buf);
    for (let k = 0; k < 12; k += 1) {
      addShape(
        path,
        cx + st.buf[k * 3],
        cy + st.buf[k * 3 + 1],
        stamp.size,
        MILL_SHAPES[stamp.sides],
        st.buf[k * 3 + 2]
      );
    }
  }

  ctx.globalAlpha = 0.85;
  buckets.forEach((path, ink) => {
    ctx.fillStyle = MILL_INK[ink];
    ctx.fill(path);
  });
  ctx.globalAlpha = 1;
};

/* ------------------------------------------------------------- the sound stack */

/**
 * Six words falling down a stack of rules, marked where a rule bit them.
 *
 * Hand-authored rather than derived from the real rule set. Importing the sound-change
 * module here would pull the entire stack — every rule, note and etymon — into the
 * landing page bundle to place thirty dots, and the figure is a schematic anyway: what
 * it has to show is the *sparseness*, and that two neighbouring words run together and
 * then come apart. Columns 1 and 2 are identical until the fourth row, where one takes
 * a voicing rule the other misses. That is father and brother.
 */
const STACK_INK = [
  'rgba(201,154,78,0.92)', // consonant shift
  'rgba(91,143,168,0.92)', // voicing
  'rgba(181,67,58,0.92)', // accent
  'rgba(125,154,94,0.92)', // vowel
  'rgba(196,202,214,0.75)', // loss
];

const STACK_GRID = [
  '000.0.',
  '...0.0',
  '000..0',
  '.1.1..',
  '222222',
  '....4.',
  '.11..1',
  '...33.',
  '4.44.4',
  '33.333',
];

const makeStack = () => ({ rows: STACK_GRID.length, cols: 6 });

const drawStack = (ctx, w, time, st) => {
  const DRAW = 7000;
  const HOLD = 1500;
  const p = Math.min(1, (time % (DRAW + HOLD)) / DRAW);

  const top = 12;
  const bottom = H - 12;
  const step = (bottom - top) / (st.rows - 1);
  const left = w * 0.16;
  const gap = (w * 0.68) / (st.cols - 1);
  const head = top + p * (bottom - top);

  // The tracks first, so every dot lands on top of its own column.
  ctx.strokeStyle = 'rgba(255,255,255,0.09)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let c = 0; c < st.cols; c += 1) {
    const x = left + c * gap;
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
  }
  ctx.stroke();

  // The rules, drawn only where the cascade has already passed — so the stack builds
  // downward rather than sitting there waiting to be filled in.
  ctx.strokeStyle = 'rgba(255,255,255,0.06)';
  ctx.beginPath();
  for (let r = 0; r < st.rows; r += 1) {
    const y = top + r * step;
    if (y > head) break;
    ctx.moveTo(left - gap * 0.45, y);
    ctx.lineTo(left + (st.cols - 1) * gap + gap * 0.45, y);
  }
  ctx.stroke();

  for (let r = 0; r < st.rows; r += 1) {
    const y = top + r * step;
    if (y > head) break;
    for (let c = 0; c < st.cols; c += 1) {
      const ink = STACK_GRID[r][c];
      if (ink === '.') continue;
      ctx.fillStyle = STACK_INK[Number(ink)];
      ctx.beginPath();
      ctx.arc(left + c * gap, y, 2.6, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  if (p < 1) {
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.beginPath();
    ctx.moveTo(left - gap * 0.45, head);
    ctx.lineTo(left + (st.cols - 1) * gap + gap * 0.45, head);
    ctx.stroke();
  }
};

/* ---------------------------------------------------------------------------- */

/* ---------------------------------------------------------------------- kana */

/**
 * 書 with its last kana cycling through the five it is allowed to be, and the row it
 * is drawing from underneath.
 *
 * Text rather than shapes, so it depends on the platform's Japanese font the way the
 * essay does. The stack is the same one src/styles/verbs.css declares — no webfont is
 * loaded for forty characters.
 */
const JP_FONT =
  '"Hiragino Sans", "Hiragino Kaku Gothic ProN", "Yu Gothic", "Noto Sans JP", Meiryo, sans-serif';
const KANA_ROW = ['か', 'き', 'く', 'け', 'こ'];
const KANA_INK = '#5fd0e8';

const makeKana = () => ({ row: KANA_ROW });

const drawKana = (ctx, w, time, st) => {
  const STEP = 1250;
  const pos = time / STEP;
  const i = Math.floor(pos) % st.row.length;
  // Eased over the first fifth of each beat, so the slot lands rather than blinks.
  const t = Math.min(1, (pos % 1) / 0.2);
  const e = d3.easeCubicOut(t);

  const size = Math.min(46, w * 0.2);
  const cx = w / 2;
  const wordY = H * 0.38;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `500 ${size}px ${JP_FONT}`;

  ctx.fillStyle = '#f5f5f7';
  ctx.fillText('書', cx - size * 0.56, wordY);

  ctx.save();
  ctx.globalAlpha = 0.25 + 0.75 * e;
  ctx.fillStyle = KANA_INK;
  ctx.fillText(st.row[i], cx + size * 0.56, wordY - (1 - e) * size * 0.22);
  ctx.restore();

  // The row it is drawing from, with the current slot lit.
  const small = Math.min(17, w * 0.075);
  const gap = small * 1.85;
  const rowY = H * 0.78;
  ctx.font = `500 ${small}px ${JP_FONT}`;
  st.row.forEach((k, j) => {
    const x = cx + (j - (st.row.length - 1) / 2) * gap;
    ctx.fillStyle = j === i ? KANA_INK : 'rgba(245,245,247,0.3)';
    ctx.fillText(k, x, rowY);
  });
};

/**
 * The suffix stack: 食べ picking up させ, られ, たく, な, かった one at a time, then
 * starting over. Segments rather than glyphs — at this size the kana would be four
 * pixels wide — so what the card shows is the shape of the thing, a word getting
 * longer by having parts added to the end of it.
 */
const STACK_PARTS = [2, 2, 2, 2, 1, 3];

const makeSuffix = () => ({
  total: STACK_PARTS.reduce((a, b) => a + b, 0),
});

const drawSuffix = (ctx, w, time, st) => {
  const STEP = 900;
  const HOLD = 1600;
  const cycle = STEP * STACK_PARTS.length + HOLD;
  const phase = time % cycle;
  const shown = Math.min(STACK_PARTS.length, Math.floor(phase / STEP) + 1);

  const pad = w * 0.08;
  const unit = (w - pad * 2) / st.total;
  const y = H * 0.46;
  const h = 26;

  let x = pad;
  STACK_PARTS.forEach((len, i) => {
    const width = unit * len - 3;
    if (i < shown) {
      // The newest segment eases in rather than appearing, so the eye follows the
      // growth instead of the blink.
      const t = Math.min(1, ((phase % STEP) / STEP) * 2.2);
      const e = i === shown - 1 ? d3.easeCubicOut(t) : 1;
      ctx.globalAlpha = 0.25 + 0.75 * e;
      ctx.fillStyle = i === 0 ? '#f5f5f7' : i % 2 ? KANA_INK : '#ffb069';
      ctx.beginPath();
      ctx.rect(x, y - h / 2 + (1 - e) * 5, width, h);
      ctx.fill();
      ctx.globalAlpha = 1;
    } else {
      ctx.strokeStyle = 'rgba(255,255,255,0.12)';
      ctx.lineWidth = 1;
      ctx.strokeRect(x + 0.5, y - h / 2 + 0.5, width - 1, h - 1);
    }
    x += unit * len;
  });

  // A rule under the whole word, so the segments read as one thing being extended.
  ctx.strokeStyle = 'rgba(255,255,255,0.16)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(pad, y + h / 2 + 9.5);
  ctx.lineTo(pad + unit * STACK_PARTS.slice(0, shown).reduce((a, b) => a + b, 0) - 3, y + h / 2 + 9.5);
  ctx.stroke();
};

/* ----------------------------------------------------------------- adjective */

/**
 * The two adjective classes, one above the other. The top word's tail cycles through
 * its own endings; the bottom word sits perfectly still while a separate block behind
 * it does the same cycling. Which is the entire distinction, and the only version of
 * it that survives being drawn 40 pixels tall.
 */
const I_TAILS = ['い', 'く', 'かっ'];
const NA_TAILS = ['だ', 'じゃ', 'だっ'];

const makeAdjective = () => ({ tails: I_TAILS });

const drawAdjective = (ctx, w, time, st) => {
  const STEP = 1300;
  const i = Math.floor(time / STEP) % st.tails.length;
  const t = Math.min(1, ((time % STEP) / STEP) * 4);
  const e = d3.easeCubicOut(t);

  const size = Math.min(30, w * 0.13);
  const cx = w / 2;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.font = `500 ${size}px ${JP_FONT}`;

  const row = (y, stem, tail, moving) => {
    const stemW = ctx.measureText(stem).width;
    const x = cx - (stemW + size * 1.6) / 2;

    ctx.fillStyle = '#f5f5f7';
    ctx.fillText(stem, x, y);

    // The い row's tail is part of the word; the な row's is a block standing behind
    // it, drawn boxed to say so.
    ctx.save();
    ctx.globalAlpha = 0.35 + 0.65 * e;
    if (moving) {
      ctx.fillStyle = KANA_INK;
      ctx.fillText(tail, x + stemW, y);
    } else {
      const pad = 4;
      const tw = ctx.measureText(tail).width;
      ctx.fillStyle = 'rgba(255,176,105,0.14)';
      ctx.fillRect(x + stemW + pad, y - size * 0.62, tw + pad * 2, size * 1.24);
      ctx.fillStyle = '#ffb069';
      ctx.fillText(tail, x + stemW + pad * 2, y);
    }
    ctx.restore();
  };

  row(H * 0.34, '高', I_TAILS[i], true);
  row(H * 0.7, '静か', NA_TAILS[i], false);
};

const KINDS = {
  kana: { make: makeKana, draw: drawKana },
  suffix: { make: makeSuffix, draw: drawSuffix },
  adjective: { make: makeAdjective, draw: drawAdjective },
  circlepack: { make: makePack, draw: drawPack },
  heatmap: { make: makeHeat, draw: drawHeat },
  globe: { make: makeGlobe, draw: drawGlobe },
  microbe: { make: makeMicrobe, draw: drawMicrobe },
  spiro: { make: makeSpiro, draw: drawSpiro },
  mill: { make: makeMill, draw: drawMill },
  grimm: { make: makeStack, draw: drawStack },
};

const EssayCardGraphic = ({ kind }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const spec = KINDS[kind];
    const canvas = canvasRef.current;
    if (!spec || !canvas) return undefined;

    const ctx = canvas.getContext('2d');
    const st = spec.make();
    let w = 0;
    let raf = null;
    let visible = false;
    let last = 0;

    const reduce =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const size = () => {
      const dpr = window.devicePixelRatio || 1;
      w = canvas.clientWidth || 300;
      canvas.width = w * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      if (now - last < 1000 / FPS) return;
      last = now;
      ctx.clearRect(0, 0, w, H);
      spec.draw(ctx, w, now, st);
    };

    const start = () => {
      if (raf || reduce) return;
      last = 0;
      raf = requestAnimationFrame(frame);
    };
    const stop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = null;
    };

    size();
    // One representative frame regardless, so a paused card is never blank.
    ctx.clearRect(0, 0, w, H);
    spec.draw(ctx, w, 900, st);

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) start();
        else stop();
      },
      { rootMargin: '80px' }
    );
    io.observe(canvas);

    const ro = new ResizeObserver(() => {
      size();
      ctx.clearRect(0, 0, w, H);
      spec.draw(ctx, w, last || 900, st);
    });
    ro.observe(canvas);

    return () => {
      stop();
      io.disconnect();
      ro.disconnect();
    };
  }, [kind]);

  return (
    <span className="essay-card-figure" aria-hidden="true">
      <canvas ref={canvasRef} style={{ width: '100%', height: `${H}px`, display: 'block' }} />
    </span>
  );
};

export default EssayCardGraphic;
